<template>
  <section aria-label="Assembly variants and location availability">
    <h4>{{ title }}</h4>
    <p>
      Compare each confirmed family link against explicit attributes. Matching
      supplied attributes is not full assembly qualification.
    </p>
    <p>
      {{ coverage.compared }} variants compared of
      {{ coverage.confirmedLinks }} links · {{ coverage.stockReads }} stock
      reads.
    </p>
    <p v-if="coverage.partial" role="status">
      Variant limit reached; this is a partial family comparison.
    </p>
    <article v-for="variant in variants" :key="String(variant.mappingId)">
      <h5>
        {{ variant.erpSystem }} {{ variant.materialNumber }} —
        {{ record(variant.assemblyFit).status }}
      </h5>
      <ul>
        <li v-for="(attribute, index) in attributes(variant)" :key="index">
          {{ attribute.field }}: required {{ value(attribute.required) }} / ERP
          {{ value(attribute.erp) }} — {{ attribute.status }}
        </li>
      </ul>
      <table v-if="locations(variant).length">
        <thead>
          <tr>
            <th>Plant</th>
            <th>Stock status</th>
            <th>Available</th>
            <th>Reserved</th>
            <th>Unit</th>
            <th>Returned source</th>
            <th>Read at</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="location in locations(variant)"
            :key="String(location.plant)"
          >
            <td>{{ location.plant }}</td>
            <td>{{ location.status }}</td>
            <td>{{ value(location.available) }}</td>
            <td>{{ value(location.reserved) }}</td>
            <td>{{ unit(location.unit) }}</td>
            <td>{{ value(location.materialDataSource) }}</td>
            <td>{{ location.checkedAt }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else>
        Stock not requested because supplied assembly attributes differ.
      </p>
    </article>
    <p v-if="!variants.length">No confirmed links for the selected source.</p>
    <ul>
      <li v-for="warning in warnings" :key="warning">{{ warning }}</li>
    </ul>
    <details>
      <summary>Technical evidence</summary>
      <pre>{{ JSON.stringify(result, null, 2) }}</pre>
    </details>
  </section>
</template>
<script setup lang="ts">
import { computed } from "vue";
const props = defineProps<{ result: Record<string, unknown>; title: string }>();
const record = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
const rows = (v: unknown) => (Array.isArray(v) ? v.map(record) : []);
const variants = computed(() => rows(props.result.variants));
const coverage = computed(() => record(props.result.coverage));
const warnings = computed(() =>
  Array.isArray(props.result.warnings) ? props.result.warnings.map(String) : []
);
const attributes = (variant: Record<string, unknown>) =>
  rows(record(variant.assemblyFit).attributes);
const locations = (variant: Record<string, unknown>) => rows(variant.locations);
const value = (v: unknown) => (v == null || v === "" ? "Unknown" : String(v));
const unit = (v: unknown) =>
  typeof v === "string" ? v : value(record(v).unit || record(v).code);
</script>
<style scoped>
section {
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  padding: 1rem;
  margin: 1rem 0;
  overflow-x: auto;
}
article {
  border-top: 1px solid var(--surface-border);
  padding: 1rem 0;
}
table {
  width: 100%;
  text-align: left;
}
th,
td {
  padding: 0.5rem;
}
pre {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 25rem;
  overflow: auto;
}
</style>
